// useAuthRedirectManager.js
import { useRef, useMemo, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { LOCAL_STORAGE_DELAYED_SERVICE_CHANGE } from "const/localStorageKeys";
import {
    servicePick, compareTwoArrays, getManagedServices, isNotSavablePath,
    normalizeClientPathForTest, normalizeClientPathForServices
} from "utils";
import usePrevious from "hooks/usePrevious";
import {
    APP_TYPE_ADMIN,
    APP_TYPE_CLIENT,
    DEFAULT_AUTH_PATH_ADMIN,
    DEFAULT_AUTH_PATH_ADMIN_RATEMOD,
    DEFAULT_AUTH_PATH_CLIENT,
    DEFAULT_AUTH_PATH_CLIENT_TEST,
    DEFAULT_NOT_AUTH_PATH,
    NOT_SAVABLE_PREFIXES,
    TEST_ALLOWED_PREFIXES,
    transformed_service_data,
    transformed_service_reverse_data,
} from "const";
import {
    LS_LAST_PAGE,
    LS_LAST_SERVICE,
    LS_SESSION_EXPIRED
} from "const/localStorageKeys";
import { readLastVisitedWithTTL } from "utils/lastVisited";

// const LS_LAST_PAGE = "LAST_PAGE_BEFORE_SESSION_EXPIRED";
// const LS_LAST_SERVICE = "LAST_SERVICE_BEFORE_SESSION_EXPIRED";

// voice|sms|... з урла
const getServiceKeyFromSearch = (search) => {
    const sp = new URLSearchParams(search || "");
    const v = sp.get("service");
    return isValidServiceKey(v) ? v : null;
};

const setServiceKeyToSearch = (search, serviceKey) => {
    const sp = new URLSearchParams(search || "");
    if (!serviceKey) {
        sp.delete("service");
        const s = sp.toString();
        return s ? `?${s}` : "";
    }
    sp.set("service", serviceKey);
    const s = sp.toString();
    return s ? `?${s}` : "";
};

const isValidServiceKey = (k) => {
    return ["voice", "sms"].includes(k);
};

const buildPathWithSearch = (pathname, search) => {
    return `${pathname}${search || ""}`;
};

/**
    * Hook that manages auth-related redirects and service synchronization.
    * Call it in the root component (e.g., App.js).
    * @param {{
    *   auth: boolean,
    *   appType: string,
    *   role_list: string[],
    *   service: string,
    *   setService: (service: string) => void,
    *   serviceDialer?: string,
    *   setDialerService?: (service: string) => void,
    *   authInfo: object,
    *   maintenanceMode: boolean
    * }} props
    * @return {boolean} bootstrapped - whether the auth redirect manager has completed its initial processing.
*/

export default function useAuthRedirectManager({
    auth,
    appType,
    role_list,
    service, // redux service (admin)
    setService, // action
    serviceDialer, // redux dialer service (client)
    setDialerService, // action
    authInfo,
    maintenanceMode
}) {
    const authReady =
        auth &&
        !!authInfo?.session?.session_key;

    const history = useHistory();
    const location = useLocation();

    const prevAuth = usePrevious(auth);
    const prevRoleList = usePrevious(role_list);

    // Guard to avoid multiple redirects
    const lastAppliedRef = useRef({
        authTransitionHandled: false,
        lastSyncKey: null, // `${pathname}|${search}|${service}`
    });

    const managedServices = useMemo(() => getManagedServices(authInfo), [authInfo]);

    const pickAllowed = (k) =>
        isValidServiceKey(k) && (!managedServices?.length || managedServices.includes(k)) ? k : null;


    const didHandleLoginRef = useRef(false);
    const [bootstrapped, setBootstrapped] = useState(true);

    // When logout -> bootstrap not needed
    useEffect(() => {
        if (!auth || maintenanceMode) {
            didHandleLoginRef.current = false;
            setBootstrapped(true);
        }
    }, [auth, maintenanceMode]);

    // When login started -> lock ONE TIME bootstrap
    useEffect(() => {
        if (!auth || maintenanceMode) return;

        // Only for transition false -> true
        if (prevAuth === false) {
            didHandleLoginRef.current = false;
            setBootstrapped(false);
        }
    }, [auth, maintenanceMode, prevAuth]);

    // When not authorized -> remember the page from which the user was logged out (once)
    useEffect(() => {
        if (auth) return;

        const { pathname, search } = window.location;
        if (isNotSavablePath(pathname)) return;

        // Do not overwrite if already saved
        const savedPage = localStorage.getItem(LS_LAST_PAGE);
        if (!savedPage) {
            localStorage.setItem(LS_LAST_PAGE, pathname);

            const sk = getServiceKeyFromSearch(search);
            if (sk) localStorage.setItem(LS_LAST_SERVICE, sk);
        }

        lastAppliedRef.current.authTransitionHandled = false;
    }, [auth]);

    // On auth established -> redirect to last visited / default page
    useEffect(() => {
        if (!authReady || maintenanceMode) return;
        if (didHandleLoginRef.current) return;

        // ключове: якщо ми сидимо на /login і authReady=true - треба редіректити завжди
        const mustRedirectFromLogin = location.pathname === "/login";
        const isRoot = location.pathname === "/";

        const authJustHappened = prevAuth === false && auth === true;
        // 0) Чи це restore після SESSION_EXPIRED?
        const isSessionExpiredFlow = localStorage.getItem(LS_SESSION_EXPIRED) === "1";
        const lastVisited = isSessionExpiredFlow ? readLastVisitedWithTTL(managedServices) : null;

        const shouldBootstrapRedirect =
            mustRedirectFromLogin || isRoot || authJustHappened || isSessionExpiredFlow;

        if (!shouldBootstrapRedirect) {
            didHandleLoginRef.current = true;
            setBootstrapped(true);
            return;
        }

        // 1) Сервіс (admin)
        const lastServiceKeyRaw = localStorage.getItem(LS_LAST_SERVICE);
        const lastServiceKey = isValidServiceKey(lastServiceKeyRaw) ? lastServiceKeyRaw : null;

        const lastServiceAllowed =
            lastServiceKey && managedServices?.length
                ? managedServices.includes(lastServiceKey)
                : true;

        if (lastServiceKey && !lastServiceAllowed) {
            localStorage.removeItem(LS_LAST_PAGE);
            localStorage.removeItem(LS_LAST_SERVICE);
        }

        const safeLastServiceKey = lastServiceAllowed ? lastServiceKey : null;

        const urlServiceKey = getServiceKeyFromSearch(location.search);
        const fallbackServiceKey = managedServices?.length
            ? (managedServices.includes("voice") ? "voice" : managedServices[0])
            : "voice";

        let desiredServiceKey = null;

        if (appType === APP_TYPE_ADMIN) {
            if (managedServices?.length === 1) {
                desiredServiceKey = managedServices[0];
            } else {
                const reduxServiceKey = transformed_service_reverse_data[service];

                desiredServiceKey =
                    pickAllowed(urlServiceKey) ||
                    pickAllowed(safeLastServiceKey) ||
                    pickAllowed(reduxServiceKey) ||
                    fallbackServiceKey;
            }

            const reduxServiceKey = transformed_service_reverse_data[service];

            if (isValidServiceKey(desiredServiceKey) && desiredServiceKey !== reduxServiceKey) {
                setService(transformed_service_data[desiredServiceKey]);
            }
        }

        // 2) targetPathname: restore має пріоритет (але тільки якщо SESSION_EXPIRED=1 і TTL валідний)
        const ratemodRole =
            role_list?.includes?.("Ratemod") || role_list?.includes?.("Ratemod (sms)");
        const supplierPath = desiredServiceKey === "sms" ? "/sms-suppliers" : "/suppliers";

        let targetPathname;

        if (appType === APP_TYPE_ADMIN) {
            if (ratemodRole) {
                targetPathname = DEFAULT_AUTH_PATH_ADMIN_RATEMOD;
            } else if (isSessionExpiredFlow && lastVisited?.pathname) {
                targetPathname = lastVisited.pathname;
            } else {
                const lastPage = localStorage.getItem(LS_LAST_PAGE);
                targetPathname = lastPage || DEFAULT_AUTH_PATH_ADMIN;
            }

            if (ratemodRole) {
                const allowed = [DEFAULT_AUTH_PATH_ADMIN_RATEMOD, supplierPath];
                if (!allowed.includes(targetPathname)) {
                    targetPathname = DEFAULT_AUTH_PATH_ADMIN_RATEMOD;
                }
            }
        } else if (appType === APP_TYPE_CLIENT) {
            if (isSessionExpiredFlow && lastVisited?.pathname) {
                targetPathname = lastVisited.pathname;
            } else {
                const lastPage = localStorage.getItem(LS_LAST_PAGE);
                targetPathname = lastPage || `${DEFAULT_AUTH_PATH_CLIENT}-voice`;
            }
        } else {
            targetPathname = DEFAULT_NOT_AUTH_PATH;
        }

        if (appType === APP_TYPE_CLIENT) {
            const isClientTest = !!authInfo?.session?.is_test;

            // список сервісів поточного акаунта після логіну
            const services = Array.from(new Set(
                (authInfo?.session?.account_list || [])
                    .filter(a => a?.active)
                    .map(a => a?.service)
                    .filter(Boolean)
            ));

            const fallbackClient =
                services.length === 2
                    ? `${DEFAULT_AUTH_PATH_CLIENT}-voice`
                    : DEFAULT_AUTH_PATH_CLIENT;

            // test фільтер
            if (isClientTest) {
                targetPathname = normalizeClientPathForTest(targetPathname);
            }

            // сервісна нормалізація (-voice/-sms)
            targetPathname = normalizeClientPathForServices(targetPathname, services, fallbackClient);
        }

        // 3) targetSearch: restore search, або сформувати service= для адмінки
        let targetSearch = isSessionExpiredFlow && lastVisited?.search ? lastVisited.search : location.search;

        if (appType === APP_TYPE_ADMIN) {
            const finalServiceKey =
                pickAllowed(desiredServiceKey) ||
                pickAllowed(getServiceKeyFromSearch(targetSearch)) ||
                pickAllowed(localStorage.getItem(LS_LAST_SERVICE)) ||
                fallbackServiceKey;

            targetSearch = setServiceKeyToSearch(targetSearch, finalServiceKey);
        }

        const needRedirect =
            mustRedirectFromLogin ||
            location.pathname !== targetPathname ||
            location.search !== targetSearch;

        if (needRedirect) {
            history.replace({ pathname: targetPathname, search: targetSearch });
        }

        // чистимо тільки тут (один раз)
        localStorage.removeItem(LS_LAST_PAGE);
        localStorage.removeItem(LS_LAST_SERVICE);
        localStorage.removeItem(LS_SESSION_EXPIRED);
        localStorage.removeItem(LOCAL_STORAGE_DELAYED_SERVICE_CHANGE);

        didHandleLoginRef.current = true;
        setBootstrapped(true);
    }, [
        authReady,
        auth,
        prevAuth,
        maintenanceMode,
        appType,
        role_list,
        service,
        setService,
        managedServices,
        history,
        location.pathname,
        location.search,
        authInfo
    ]);

    // While in normal operation (auth=true): synchronize ?service= with redux service (admin)
    useEffect(() => {
        if (!auth || maintenanceMode) return;
        if (appType !== APP_TYPE_ADMIN) return;

        //  НІКОЛИ не синхронізуємо service на /login
        if (location.pathname === "/login") return;

        // Поки ми не завершили login-редірект — не ліземо в URL
        if (!authReady) return;

        // const raw = new URLSearchParams(location.search || "").get("service");
        const urlServiceKey = getServiceKeyFromSearch(location.search); // вже sanitized
        const reduxServiceKey = transformed_service_reverse_data[service]; // "voice"/"sms"

        if (urlServiceKey && managedServices?.length && !managedServices.includes(urlServiceKey)) {
            const fallback =
                managedServices.includes("voice") ? "voice" : managedServices[0];

            const nextSearch = setServiceKeyToSearch(location.search, fallback);

            if (nextSearch !== location.search) {
                history.replace({ pathname: location.pathname, search: nextSearch });
            }

            if (fallback !== reduxServiceKey) {
                setService(transformed_service_data[fallback]);
            }
            return;
        }

        if (!urlServiceKey && isValidServiceKey(reduxServiceKey)) {
            const cur = history.location;
            const nextSearch = setServiceKeyToSearch(cur.search, reduxServiceKey);
            if (nextSearch !== cur.search) {
                history.replace({ pathname: cur.pathname, search: nextSearch });
            }
            return;
        }
        if (urlServiceKey && urlServiceKey !== reduxServiceKey) {
            setService(transformed_service_data[urlServiceKey]);
            return;
        }

        // Якщо role_list змінився (Наприклад рейтмод додано/видалено):
        if (prevRoleList && role_list && !compareTwoArrays(role_list, prevRoleList)) {
            // Custom logic when roles change
            // Add if needed
        }
    }, [
        auth,
        maintenanceMode,
        appType,
        managedServices,
        location.pathname,
        location.search,
        history,
        service,
        setService,
        role_list,
        prevRoleList,
    ]);

    return bootstrapped;
};
